import { forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo-praxis-ib.png";
import enterosanLogo from "@/assets/enterosan-logo.png";
import laborDostalLogo from "@/assets/labor-dostal-logo.jpg";
import microgendxLogo from "@/assets/microgendx-logo.webp";
import ganzimmunLogo from "@/assets/ganzimmun-logo.jpg";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

import { useT, useLocalePath, useAnchorHref } from "@/i18n";


interface DoctorGroup {
  field: string;
  doctors: { name: string; url: string; note?: string }[];
}

const DOCTOR_GROUPS_DE: DoctorGroup[] = [
  {
    field: "Urologie & Andrologie",
    doctors: [
      { name: "Dr. Anne-Catherine Piskernik", url: "https://urologin.wien/", note: "Wahlärztin – 1010 Wien" },
    ],
  },
  {
    field: "Allgemeinmedizin & Ganzheitsmedizin",
    doctors: [
      { name: "Dr. Veronika Königswieser", url: "https://www.drkoenigswieser.com/", note: "Praktische Ärztin, Mikroimmuntherapie – 1140 Wien" },
      { name: "Dr. med. Clara Kleinhans", url: "https://ganzheitlich-doc.at/", note: "Allgemeinmedizin, Ganzheitsmedizin" },
    ],
  },
  {
    field: "Innere Medizin & Gastroenterologie",
    doctors: [
      { name: "Prim. Dr. Marcus Franz, MSc", url: "https://internist-hietzing.at/", note: "Innere Medizin, Vorsorge-Coloskopie – 1130 Wien" },
    ],
  },
  {
    field: "Gynäkologie & Geburtshilfe",
    doctors: [
      { name: "OÄ Dr. Sarwenaz Mahrouzadeh-Muqolli", url: "https://mz-wien.at/dr-sarwenaz-mahrouzadeh-muqolli/", note: "Medizin im Zentrum, 1010 Wien" },
    ],
  },
  {
    field: "Orthopädie",
    doctors: [
      { name: "Dr. Luschnig & Dr. Leitner – Orthopädie im Dritten", url: "https://www.orthopaedie-im-dritten.at/", note: "1030 Wien" },
      { name: "Mag. Dr. Manfred Neumaier, LL.M.", url: "https://www.neumaier.at/", note: "Villa Medica – Mödling & Pottendorf" },
    ],
  },
  {
    field: "Osteopathie & Physiotherapie",
    doctors: [
      { name: "Praxis Struss", url: "https://www.praxis-struss.at/", note: "Osteopathie & Physiotherapie – Wien" },
    ],
  },
];

const DOCTOR_GROUPS_EN: DoctorGroup[] = [
  {
    field: "Urology & Andrology",
    doctors: [
      { name: "Dr Anne-Catherine Piskernik", url: "https://urologin.wien/", note: "Private practice – 1010 Vienna" },
    ],
  },
  {
    field: "General & Holistic Medicine",
    doctors: [
      { name: "Dr Veronika Königswieser", url: "https://www.drkoenigswieser.com/", note: "General practitioner, micro-immunotherapy – 1140 Vienna" },
      { name: "Dr med. Clara Kleinhans", url: "https://ganzheitlich-doc.at/", note: "General and holistic medicine" },
    ],
  },
  {
    field: "Internal Medicine & Gastroenterology",
    doctors: [
      { name: "Prim. Dr Marcus Franz, MSc", url: "https://internist-hietzing.at/", note: "Internal medicine, screening colonoscopy – 1130 Vienna" },
    ],
  },
  {
    field: "Gynaecology & Obstetrics",
    doctors: [
      { name: "Dr Sarwenaz Mahrouzadeh-Muqolli", url: "https://mz-wien.at/dr-sarwenaz-mahrouzadeh-muqolli/", note: "Medizin im Zentrum, 1010 Vienna" },
    ],
  },
  {
    field: "Orthopaedics",
    doctors: [
      { name: "Dr Luschnig & Dr Leitner – Orthopädie im Dritten", url: "https://www.orthopaedie-im-dritten.at/", note: "1030 Vienna" },
      { name: "Mag. Dr Manfred Neumaier, LL.M.", url: "https://www.neumaier.at/", note: "Villa Medica – Mödling & Pottendorf" },
    ],
  },
  {
    field: "Osteopathy & Physiotherapy",
    doctors: [
      { name: "Praxis Struss", url: "https://www.praxis-struss.at/", note: "Osteopathy & physiotherapy – Vienna" },
    ],
  },
];

const Footer = forwardRef<HTMLElement>((_, ref) => {
  const currentYear = new Date().getFullYear();
  const [impressumOpen, setImpressumOpen] = useState(false);
  const [datenschutzOpen, setDatenschutzOpen] = useState(false);
  const [kiOpen, setKiOpen] = useState(false);
  const [hinweiseOpen, setHinweiseOpen] = useState(false);
  const [aerzteOpen, setAerzteOpen] = useState(false);
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  const lp = useLocalePath();
  const anchor = useAnchorHref();
  const doctorGroups = useT(DOCTOR_GROUPS_DE, DOCTOR_GROUPS_EN);

  const t = useT(
    {
      tagline: "Wo Wissenschaft auf Natur trifft – Integrative Medizin für Ihre Gesundheit.",
      quickLinks: "Schnelllinks",
      links: [
        { label: "Startseite", href: "/", anchor: false },
        { label: "Willkommen", href: "#willkommen", anchor: true },
        { label: "Leistungen", href: "#fachlicher-ansatz", anchor: true },
        { label: "Meine Philosophie", href: "#philosophie", anchor: true },
        { label: "Über mich", href: "#ueber-mich", anchor: true },
        { label: "Termine & Preise", href: "#termine", anchor: true },
        { label: "Bakteriophagen-Therapie", href: "/bakteriophagen-therapie", anchor: false },
        { label: "Darmsanierung Wien", href: "/darmsanierung-wien", anchor: false },
        { label: "Beschwerdebilder", href: "/beschwerden", anchor: false },
        { label: "Termin buchen", href: "/termin", anchor: false },
        { label: "Magazin", href: "/blog", anchor: false },
        { label: "Häufige Fragen", href: "/faq", anchor: false },
        { label: "Mein Buch", href: "/e-book", anchor: false },
        { label: "Kontakt", href: "#kontakt", anchor: true },
      ],

      contact: "Kontakt",
      address: "Linzer Straße 383/Top 7, Wien",
      phone: "Tel: 0660 404 1993",
      impressum: "Impressum",
      privacy: "Datenschutz",
      ai: "KI-Transparenz",
      notes: "Wichtige Hinweise",
      doctors: "Empfohlene Ärztinnen & Ärzte",
      cooperation: "In Kooperation mit",
      rights: `© ${currentYear} Praxis Innere Balance. Alle Rechte vorbehalten.`,
    },
    {
      tagline: "Where science meets nature – integrative medicine for your health.",
      quickLinks: "Quick links",
      links: [
        { label: "Home", href: "/", anchor: false },
        { label: "Welcome", href: "#willkommen", anchor: true },
        { label: "Services", href: "#fachlicher-ansatz", anchor: true },
        { label: "My philosophy", href: "#philosophie", anchor: true },
        { label: "About Me", href: "#ueber-mich", anchor: true },
        { label: "Appointments & Fees", href: "#termine", anchor: true },
        { label: "Bacteriophage therapy", href: "/bakteriophagen-therapie", anchor: false },
        { label: "Gut restoration Vienna", href: "/darmsanierung-wien", anchor: false },
        { label: "Health concerns", href: "/beschwerden", anchor: false },
        { label: "Book an appointment", href: "/termin", anchor: false },
        { label: "Magazine", href: "/blog", anchor: false },
        { label: "Frequently asked questions", href: "/faq", anchor: false },
        { label: "My Book", href: "/e-book", anchor: false },
        { label: "Contact", href: "#kontakt", anchor: true },
      ],

      contact: "Contact",
      address: "Linzer Straße 383/Top 7, 1140 Vienna, Austria",
      phone: "Phone: +43 660 404 1993",
      impressum: "Legal notice",
      privacy: "Privacy policy",
      ai: "AI transparency",
      notes: "Important information",
      doctors: "Recommended doctors",
      cooperation: "In cooperation with",
      rights: `© ${currentYear} Praxis Innere Balance. All rights reserved.`,
    },
  );

  const triggerClass =
    "flex items-center gap-2 text-hero-foreground/80 hover:text-hero-foreground transition-colors text-sm font-medium w-full justify-between border-t border-hero-foreground/20 pt-4";
  const contentClass = "pt-4 text-sm text-hero-foreground/70 space-y-3";

  return (
    <footer ref={ref} className="bg-hero text-hero-foreground py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="mb-4">
                <img src={logo} alt="Praxis Innere Balance" className="h-20 w-auto" />
              </div>
              <p className="text-hero-foreground/70 text-sm">{t.tagline}</p>
            </div>

            {/* Contact */}
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold mb-4">{t.contact}</h4>
                <ul className="space-y-2 text-sm text-hero-foreground/70">
                  <li>Ärztezentrum West</li>
                  <li>{t.address}</li>
                  <li>{t.phone}</li>
                  <li>office@ibpraxis.net</li>
                </ul>
              </div>
            </div>

          </div>

          {/* Legal Sections */}
          <div className="space-y-4 mb-8">
            {/* Impressum */}
            <Collapsible open={impressumOpen} onOpenChange={setImpressumOpen}>
              <CollapsibleTrigger className={triggerClass}>
                <span>{t.impressum}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${impressumOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className={contentClass}>
                {useT(
                  <>
                    <p><strong>Impressum/Information laut §5 ECG:</strong></p>
                    <p>Informationen und Offenlegung gemäß §5 (1) ECG, § 25 MedienG, § 63 GewO und § 14 UGB</p>
                    <p><strong>Webseitenbetreiber:</strong> Isabella Bartak</p>
                    <p><strong>Firmenanschrift:</strong> Ärztezentrum West, Linzer Straße 383, Top 7 und Top 12, 1140 Wien</p>
                    <p><strong>GISA-Zahl:</strong> 35045632</p>
                    <p><strong>Gewerbeaufsichtbehörde:</strong> WKO Wien</p>
                    <p><strong>Mitgliedschaften:</strong> WKO Wien</p>
                    <p><strong>Kontaktdaten:</strong><br />Telefon: 06604041993<br />Email: office@ibpraxis.net</p>
                    <p><strong>Gewerbetätigkeit:</strong> Einzelunternehmen</p>
                    <p><strong>Firmenbuchnummer:</strong> 587309s</p>
                    <p><strong>Urheberrecht:</strong> Die Inhalte dieser Webseite unterliegen, soweit dies rechtlich möglich ist, diversen Schutzrechten (z.B dem Urheberrecht). Jegliche Verwendung/Verbreitung von bereitgestelltem Material, welche urheberrechtlich untersagt ist, bedarf schriftlicher Zustimmung des Webseitenbetreibers.</p>
                    <p><strong>Haftungsausschluss:</strong> Trotz sorgfältiger inhaltlicher Kontrolle übernimmt der Webseitenbetreiber dieser Webseite keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.</p>
                    <p className="italic"><strong>Rechtlicher Hinweis und wichtige Klienteninformation:</strong><br />
                    Meine Arbeit beruht auf komplementären Methoden, daher kann ich den Besuch beim Arzt nicht ersetzen. Meine Arbeit stellt somit keinen Ersatz für eine medizinische Diagnose oder Behandlung dar. Eine schulmedizinische Behandlung oder Therapie sollte daher nicht ab- bzw. unterbrochen, eine notwendige nicht unterlassen, bzw. hinausgeschoben werden.</p>
                  </>,
                  <>
                    <p><strong>Legal notice (Impressum) pursuant to § 5 ECG:</strong></p>
                    <p>Information and disclosure in accordance with § 5 (1) ECG, § 25 MedienG, § 63 GewO and § 14 UGB (Austrian law).</p>
                    <p><strong>Website operator:</strong> Isabella Bartak</p>
                    <p><strong>Business address:</strong> Ärztezentrum West, Linzer Straße 383, Top 7 and Top 12, 1140 Vienna, Austria</p>
                    <p><strong>GISA number:</strong> 35045632</p>
                    <p><strong>Supervisory trade authority:</strong> WKO Vienna</p>
                    <p><strong>Memberships:</strong> WKO Vienna</p>
                    <p><strong>Contact:</strong><br />Phone: +43 660 404 1993<br />Email: office@ibpraxis.net</p>
                    <p><strong>Business form:</strong> Sole proprietorship</p>
                    <p><strong>Commercial register number:</strong> 587309s</p>
                    <p><strong>Copyright:</strong> As far as legally possible, the content of this website is protected by copyright and other rights. Any use or distribution of the material provided that is prohibited under copyright law requires the written consent of the website operator.</p>
                    <p><strong>Disclaimer:</strong> Despite careful review of the content, the operator of this website accepts no liability for the content of external links. The operators of the linked pages are solely responsible for their content.</p>
                    <p className="italic"><strong>Legal notice and important client information:</strong><br />
                    My work is based on complementary methods and therefore cannot replace a visit to a doctor. It does not constitute a substitute for medical diagnosis or treatment. Conventional medical treatment or therapy should not be discontinued or interrupted, and necessary treatment should not be omitted or postponed.</p>
                  </>,
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Datenschutz */}
            <Collapsible open={datenschutzOpen} onOpenChange={setDatenschutzOpen}>
              <CollapsibleTrigger className={triggerClass}>
                <span>{t.privacy}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${datenschutzOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className={contentClass}>
                {useT(
                  <>
                    <p><strong>Datenschutzerklärung</strong></p>
                    <p>Der Schutz Ihrer persönlichen Daten ist uns ein besonderes Anliegen. Wir verarbeiten Ihre Daten daher ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, TKG 2003).</p>
                    <p><strong>Verantwortliche Stelle:</strong><br />Isabella Bartak<br />Ärztezentrum West, Linzer Straße 383, Top 7 und Top 12, 1140 Wien<br />Email: office@ibpraxis.net</p>
                    <p><strong>Erhebung und Verarbeitung von Daten:</strong><br />Beim Besuch unserer Website werden automatisch technische Informationen erfasst (z.B. IP-Adresse, Browsertyp, Besuchszeit). Diese Daten werden nur zur technischen Bereitstellung der Website verwendet und nach kurzer Zeit gelöscht.</p>
                    <p><strong>Kontaktformular:</strong><br />Wenn Sie uns über das Kontaktformular kontaktieren, werden Ihre angegebenen Daten zur Bearbeitung Ihrer Anfrage gespeichert. Diese Daten werden nicht ohne Ihre Einwilligung weitergegeben.</p>
                    <p><strong>Advent-Gewinnspiel:</strong><br />Mit Ihrer Einwilligung (Art. 6 Abs. 1 lit. a DSGVO) verarbeiten wir Ihren Namen, Ihre E-Mail-Adresse, Postadresse und die Teilnahmen je Türchen für Verlosung, Gewinnbenachrichtigung und Versand. Die Daten sind nur der Veranstalterin zugänglich; notwendige Versanddaten können an den Versanddienstleister weitergegeben werden. Eine Nutzung für Werbung erfolgt nicht. Die Angaben werden spätestens drei Monate nach der Verlosung gelöscht. Sie können Ihre Einwilligung jederzeit für die Zukunft unter office@ibpraxis.net widerrufen; Ihre Lose werden dann entfernt.</p>
                    <p><strong>Ihre Rechte:</strong><br />Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch. Wenn Sie glauben, dass die Verarbeitung Ihrer Daten gegen das Datenschutzrecht verstößt, können Sie sich bei der Aufsichtsbehörde beschweren.</p>
                    <p><strong>Cookies:</strong><br />Diese Website verwendet nur technisch notwendige Cookies, die für den Betrieb der Website erforderlich sind.</p>
                    <p><strong>KI-Assistent „Mikrobi“:</strong><br />Für die Beantwortung allgemeiner Fragen zu dieser Website steht ein KI-Assistent zur Verfügung. Ihre Eingaben werden ausschließlich zur Erzeugung der Antwort an einen Dienstleister für KI-Modelle übermittelt und dort weder gespeichert noch zum Training verwendet. Der Wortlaut Ihrer Fragen wird nicht gespeichert; erfasst wird lediglich anonym, welchem Themenbereich eine Frage zuzuordnen war (z. B. „Termin“ oder „Reizdarm“), um die Inhalte dieser Website zu verbessern. Eine Zuordnung zu einer Person oder einem Klientenkonto findet nicht statt, IP-Adressen werden nicht gespeichert. Der Gesprächsverlauf besteht nur im Browser während Ihres Besuchs. Bitte geben Sie im Chat keine Gesundheits- oder personenbezogenen Daten ein.</p>
                    <p><strong>Keine automatisierte Entscheidungsfindung:</strong><br />Es findet keine automatisierte Entscheidungsfindung einschließlich Profiling statt. Gesundheitsdaten von Klientinnen und Klienten werden nicht in KI-Systeme eingespeist.</p>
                  </>,
                  <>
                    <p><strong>Privacy policy</strong></p>
                    <p>Protecting your personal data matters to us. We therefore process your data exclusively on the basis of the applicable legal provisions (GDPR, Austrian Telecommunications Act).</p>
                    <p><strong>Data controller:</strong><br />Isabella Bartak<br />Ärztezentrum West, Linzer Straße 383, Top 7 and Top 12, 1140 Vienna, Austria<br />Email: office@ibpraxis.net</p>
                    <p><strong>Collection and processing of data:</strong><br />When you visit our website, technical information (e.g. IP address, browser type, time of visit) is recorded automatically. This data is used solely to deliver the website technically and is deleted after a short period.</p>
                    <p><strong>Contact form:</strong><br />If you contact us via the contact form, the details you provide are stored in order to process your enquiry. This data is not passed on to third parties without your consent.</p>
                    <p><strong>Advent raffle:</strong><br />With your consent (Art. 6(1)(a) GDPR), we process your name, email, postal address and door participations for the draw, winner notification and delivery. Access is restricted to the organiser; necessary delivery details may be shared with the delivery provider. The data is not used for advertising and is deleted within three months after the draw. You may withdraw consent at any time by emailing office@ibpraxis.net; your tickets will then be removed.</p>
                    <p><strong>Your rights:</strong><br />You have the right to information, rectification, erasure, restriction of processing, data portability and objection. If you believe that the processing of your data infringes data protection law, you may lodge a complaint with the supervisory authority.</p>
                    <p><strong>Cookies:</strong><br />This website uses only technically necessary cookies that are required for the site to function.</p>
                    <p><strong>AI assistant "Mikrobi":</strong><br />An AI assistant is available to answer general questions about this website. Your input is transmitted to an AI model provider solely to generate the reply and is neither stored nor used for training there. The wording of your questions is not stored; we only record anonymously which topic area a question belonged to (e.g. "appointment" or "IBS") in order to improve the content of this website. No link to a person or client record is made and no IP addresses are stored. The conversation exists only in your browser during your visit. Please do not enter health-related or personal data in the chat.</p>
                    <p><strong>No automated decision-making:</strong><br />No automated decision-making, including profiling, takes place. Client health data is never fed into AI systems.</p>
                  </>,
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* KI-Transparenz */}
            <Collapsible open={kiOpen} onOpenChange={setKiOpen}>
              <CollapsibleTrigger className={triggerClass}>
                <span>{t.ai}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${kiOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className={contentClass}>
                {useT(
                  <>
                    <p><strong>Hinweis zur Verwendung künstlicher Intelligenz</strong></p>
                    <p>Einzelne Bilder, Illustrationen und Videosequenzen auf dieser Website wurden mit Unterstützung generativer KI-Systeme erstellt bzw. gestaltet. Sie dienen ausschließlich der Veranschaulichung und bilden keine realen Personen, Befunde oder Behandlungssituationen ab.</p>
                    <p>Inhaltliche Texte werden fachlich geprüft und redaktionell verantwortet. Die Verantwortung für sämtliche veröffentlichten Inhalte liegt bei der Webseitenbetreiberin.</p>
                    <p><strong>Keine KI in der Beratung:</strong><br />In der Praxis kommen keine KI-Systeme für Diagnostik, Therapieentscheidungen, Befundinterpretation oder die Auswertung von Daten der Klientinnen und Klienten zum Einsatz. Beratung und Betreuung erfolgen ausschließlich persönlich.</p>
                    <p><strong>KI-Assistent „Mikrobi“:</strong><br />Diese Website stellt mit „Mikrobi“ einen KI-gestützten Informationsassistenten bereit. Er ist klar als KI gekennzeichnet, beantwortet ausschließlich allgemeine Fragen zu den Inhalten dieser Website und stellt keine Diagnosen, Therapieempfehlungen oder Heilversprechen. Er ersetzt keine ärztliche Abklärung und keine persönliche Beratung. Chatverläufe und Fragetexte werden nicht gespeichert und nicht mit Klientendaten verknüpft; anonym gezählt wird ausschließlich das Themengebiet einer Frage; es findet keine Emotions- oder Gesichtserkennung und keine automatisierte Entscheidungsfindung statt. Bitte übermitteln Sie im Chat keine Gesundheits- oder personenbezogenen Daten.</p>
                    <p><strong>Verantwortlich:</strong><br />Isabella Bartak, Ärztezentrum West, Linzer Straße 383, Top 7 und Top 12, 1140 Wien, office@ibpraxis.net</p>
                  </>,
                  <>
                    <p><strong>Note on the use of artificial intelligence</strong></p>
                    <p>Some images, illustrations and video sequences on this website were created or designed with the support of generative AI systems. They serve purely illustrative purposes and do not depict real people, findings or treatment situations.</p>
                    <p>All written content is reviewed for professional accuracy and is editorially supervised. Responsibility for everything published here lies with the website operator.</p>
                    <p><strong>No AI in consultations:</strong><br />No AI systems are used in the practice for diagnostics, therapeutic decisions, interpretation of findings or the evaluation of client data. Consultation and care are provided personally at all times.</p>
                    <p><strong>AI assistant "Mikrobi":</strong><br />This website provides an AI-based information assistant called "Mikrobi". It is clearly labelled as AI, answers general questions about the content of this website only and does not provide diagnoses, therapeutic recommendations or promises of cure. It does not replace medical assessment or personal consultation. Conversations and question texts are not stored and are not linked to client data; only the topic area of a question is counted anonymously; no emotion or facial recognition and no automated decision-making take place. Please do not share health-related or personal data in the chat.</p>
                    <p><strong>Responsible:</strong><br />Isabella Bartak, Ärztezentrum West, Linzer Straße 383, Top 7 and Top 12, 1140 Vienna, office@ibpraxis.net</p>
                  </>,
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Wichtige Hinweise */}
            <Collapsible open={hinweiseOpen} onOpenChange={setHinweiseOpen}>
              <CollapsibleTrigger className={triggerClass}>
                <span>{t.notes}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${hinweiseOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 text-sm text-hero-foreground/70 space-y-2">
                {useT(
                  <>
                    <p>• Termine, welche nicht mindestens 24 Stunden vorher abgesagt wurden, werden grundsätzlich verrechnet.</p>
                    <p>• Keine Kassenabrechnung möglich.</p>
                    <p>• Fragen, Anfragen, Dokumentationen und Infos werden via office@ibpraxis.net bearbeitet.</p>
                  </>,
                  <>
                    <p>• Appointments not cancelled at least 24 hours in advance are charged in full.</p>
                    <p>• Direct billing with statutory health insurers is not possible.</p>
                    <p>• Questions, enquiries, documents and information are handled via office@ibpraxis.net.</p>
                  </>,
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Empfohlene Ärzt:innen */}
            <Collapsible open={aerzteOpen} onOpenChange={setAerzteOpen}>
              <CollapsibleTrigger className={triggerClass}>
                <span>{t.doctors}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${aerzteOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 text-sm text-hero-foreground/70 space-y-5">
                <p className="italic">
                  {useT(
                    "Da die Komplementärtherapie eine ärztliche Behandlung nicht ersetzt, empfehle ich gerne folgende Kolleginnen und Kollegen:",
                    "Since complementary therapy does not replace medical treatment, I am happy to recommend the following colleagues:",
                  )}
                </p>
                {doctorGroups.map((group, idx) => (
                  <div key={idx}>
                    <h5 className="font-semibold text-hero-foreground mb-2">{group.field}</h5>
                    <ul className="space-y-2 pl-1">
                      {group.doctors.map((doc, dIdx) => (
                        <li key={dIdx}>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-hero-foreground transition-colors"
                          >
                            {doc.name}
                          </a>
                          {doc.note && (
                            <span className="block text-xs text-hero-foreground/60">{doc.note}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <p className="text-xs text-hero-foreground/60">
                  {useT(
                    "Hinweis: Persönliche Empfehlungen ohne wirtschaftliche Verbindung.",
                    "Please note: personal recommendations with no commercial affiliation.",
                  )}
                </p>
              </CollapsibleContent>
            </Collapsible>

            {/* Schnelllinks (SEO) */}
            <Collapsible open={quickLinksOpen} onOpenChange={setQuickLinksOpen}>
              <CollapsibleTrigger className={triggerClass}>
                <span>{t.quickLinks}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${quickLinksOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4">
                <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
                  {t.links.map((item) => (
                    <li key={item.label}>
                      {item.anchor ? (
                        <a href={anchor(item.href)} className="text-hero-foreground/70 hover:text-hero-foreground transition-colors">
                          {item.label}
                        </a>
                      ) : (
                        <Link to={lp(item.href)} className="text-hero-foreground/70 hover:text-hero-foreground transition-colors">
                          {item.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <div className="pt-6 border-t border-hero-foreground/20 flex flex-col items-center gap-4 mb-6">
            <span className="text-sm text-hero-foreground/70">{t.cooperation}</span>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <div className="bg-white rounded-lg px-5 py-3 flex items-center justify-center">
                <img src={enterosanLogo} alt="Enterosan Labordiagnostik" className="h-12 w-auto" />
              </div>
              <div className="bg-white rounded-lg px-5 py-3 flex items-center justify-center">
                <img src={laborDostalLogo} alt="Labor Dr. Dostal" className="h-12 w-auto" />
              </div>
              <div className="bg-white rounded-lg px-5 py-3 flex items-center justify-center">
                <img src={microgendxLogo} alt="MicroGenDX – qPCR + NGS DNA Diagnostics" className="h-12 w-auto" />
              </div>
              <div className="bg-white rounded-lg px-5 py-3 flex items-center justify-center">
                <img src={ganzimmunLogo} alt="GANZIMMUN Diagnostics" className="h-12 w-auto" />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-hero-foreground/20 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-hero-foreground/60">
            <p>{t.rights}</p>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
